// Relatório de acertos entre datas (para auditoria, fiscalização, Justiça…):
// um resumo por colaborador, com os totais, os acertos desfeitos no período e,
// se pedido, o demonstrativo completo de cada acerto. Feito no aparelho, com
// os dados que já estão nele (funciona sem sinal).
import { cpf as formatarCpf, data as formatarData, documento as formatarDocumento, numeroBR } from './formato.js';
import { desenharDemonstrativo, rodape, textoDaQuitacao } from './demonstrativo.js';

const centavos = (v) => Math.round((Number(v) || 0) * 100);
const reais = (c) => c / 100;
const porNumero = (a, b) => (a.data < b.data ? -1 : a.data > b.data ? 1 : (a.ano - b.ano) || (a.numero - b.numero));

// Os acertos fechados com data entre `de` e `ate` (inclusive), de um
// colaborador ou de todos, separados por colaborador (em ordem de nome) e em
// ordem de data. Os desfeitos no período vêm à parte (explicam os números que
// faltam na sequência).
export function relatorioDeAcertos({ de, ate, colaboradorId = null, acertos = [], colaboradores = [] }) {
  const noPeriodo = (a) => a.data >= de && a.data <= ate && (!colaboradorId || a.colaborador_id === colaboradorId);
  const validos = acertos.filter((a) => !a.deleted_at && a.status !== 'rascunho' && noPeriodo(a));
  const desfeitos = acertos.filter((a) => a.deleted_at && a.status !== 'rascunho' && noPeriodo(a)).sort(porNumero);
  const pessoa = (id) => colaboradores.find((c) => c.id === id) ?? null;

  const somar = (lista) => {
    const t = { creditos: 0, vales: 0, despesas: 0, descontos: 0, pago: 0, devolvido: 0, levado: 0, emAberto: 0 };
    for (const a of lista) {
      t.creditos += centavos(a.total_creditos);
      t.vales += centavos(a.total_vales);
      t.despesas += centavos(a.total_despesas);
      t.descontos += centavos(a.total_descontos);
      const saldo = centavos(a.saldo);
      if (a.status === 'fechado') t.emAberto += saldo;
      else if (a.quitacao === 'paga') t.pago += -saldo;
      else if (a.quitacao === 'devolvida') t.devolvido += saldo;
      else if (a.quitacao === 'transportada') t.levado += saldo;
    }
    return Object.fromEntries(Object.entries(t).map(([k, v]) => [k, reais(v)]));
  };

  const ids = [...new Set(validos.map((a) => a.colaborador_id))];
  const grupos = ids
    .map((id) => {
      const lista = validos.filter((a) => a.colaborador_id === id).sort(porNumero);
      return { colaborador: pessoa(id), acertos: lista, totais: somar(lista) };
    })
    .sort((a, b) => (a.colaborador?.nome ?? '').localeCompare(b.colaborador?.nome ?? '', 'pt-BR'));

  return {
    de,
    ate,
    colaborador: colaboradorId ? pessoa(colaboradorId) : null,
    grupos,
    totais: somar(validos),
    quantidade: validos.length,
    desfeitos: desfeitos.map((a) => ({ acerto: a, colaborador: pessoa(a.colaborador_id) })),
  };
}

const nomeDaPessoa = (c) => (c ? `${c.nome}${c.apelido ? ` (${c.apelido})` : ''}` : 'Colaborador');

// "Pago em 11/08/2026 · assinado" (a segunda linha de cada acerto no resumo).
export function situacaoNoRelatorio(a) {
  const quitacao = a.status === 'fechado' ? 'Ainda não quitado.' : textoDaQuitacao(a);
  const assinatura = a.assinatura ? `assinado ${a.assinado_via === 'link' ? 'pelo link' : 'na tela'}` : 'sem assinatura';
  return `${quitacao.replace(/\.$/, '')} · ${assinatura}`;
}

export function nomeDoArquivoDoRelatorio({ de, ate, colaborador = null }) {
  const pessoa = colaborador
    ? `-${(colaborador.apelido || colaborador.nome).normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase()}`
    : '';
  return `acertos${pessoa}-${de}-a-${ate}.pdf`;
}

// O PDF: resumo (por colaborador, com totais), desfeitos e, se `completo`, o
// demonstrativo de cada acerto numa página nova.
//   dadosDoAcerto(acerto) → o que demonstrativoEmPdf recebe (viagens, lançamentos…)
export async function relatorioEmPdf({ relatorio, empresa, emitidoEm = new Date(), completo = true, dadosDoAcerto }) {
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const margem = 18;
  const direita = 210 - margem;
  const fim = 280;
  let y = 24;
  // No meio da tabela de um colaborador, a página nova repete o cabeçalho.
  let emTabela = false;
  const cabe = (altura) => {
    if (y + altura > fim) {
      pdf.addPage();
      y = 20;
      if (emTabela) cabecalhoDaTabela();
    }
  };
  const fuso = empresa.fuso_horario || 'America/Sao_Paulo';
  const emitido = `${new Intl.DateTimeFormat('pt-BR', { timeZone: fuso, day: '2-digit', month: '2-digit', year: 'numeric' }).format(emitidoEm)}`
    + ` às ${new Intl.DateTimeFormat('pt-BR', { timeZone: fuso, hour: '2-digit', minute: '2-digit' }).format(emitidoEm)}`;
  const doc = empresa.documento ? ` · ${empresa.tipo_pessoa === 'PF' ? 'CPF' : 'CNPJ'} ${formatarDocumento(empresa.documento)}` : '';
  const valor = (v) => numeroBR(v, { casas: 2 });
  // Colunas: nº, data, período e cinco valores alinhados à direita.
  const col = { numero: margem, data: margem + 17, periodo: margem + 37, creditos: 116, vales: 135, despesas: 154, descontos: 173, saldo: direita };

  function cabecalhoDaTabela() {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(7.5);
    pdf.setTextColor(90);
    pdf.text('Nº', col.numero, y);
    pdf.text('Data', col.data, y);
    pdf.text('Período', col.periodo, y);
    for (const [chave, rotulo] of [['creditos', 'Créditos'], ['vales', 'Vales'], ['despesas', 'Despesas'], ['descontos', 'Descontos'], ['saldo', 'Saldo']]) {
      pdf.text(rotulo, col[chave], y, { align: 'right' });
    }
    pdf.setTextColor(0);
    y += 1.5;
    pdf.setLineWidth(0.2);
    pdf.line(margem, y, direita, y);
    y += 4;
  }

  function linhaDeValores(rotulos, v, { negrito = false } = {}) {
    pdf.setFont('helvetica', negrito ? 'bold' : 'normal');
    pdf.setFontSize(8.5);
    if (rotulos.numero) pdf.text(rotulos.numero, col.numero, y);
    if (rotulos.data) pdf.text(rotulos.data, col.data, y);
    if (rotulos.periodo) pdf.text(rotulos.periodo, col.periodo, y);
    pdf.text(valor(v.creditos), col.creditos, y, { align: 'right' });
    pdf.text(valor(v.vales), col.vales, y, { align: 'right' });
    pdf.text(valor(v.despesas), col.despesas, y, { align: 'right' });
    pdf.text(valor(v.descontos), col.descontos, y, { align: 'right' });
    if (v.saldo !== undefined) pdf.text(valor(v.saldo), col.saldo, y, { align: 'right' });
  }

  function linhaPequena(texto) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7.5);
    pdf.setTextColor(90);
    const linhas = pdf.splitTextToSize(texto, direita - col.data);
    pdf.text(linhas, col.data, y);
    pdf.setTextColor(0);
    y += linhas.length * 3.4 + 1.6;
  }

  const r = relatorio;
  // Cabeçalho
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(15);
  pdf.text('RELATÓRIO DE ACERTOS', margem, y);
  y += 7;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.text(`${empresa.nome}${doc}`, margem, y);
  y += 5;
  pdf.text(`Período: ${formatarData(r.de)} a ${formatarData(r.ate)} (pela data do acerto)`, margem, y);
  y += 5;
  pdf.text(`Colaborador: ${r.colaborador ? nomeDaPessoa(r.colaborador) : 'todos'}`, margem, y);
  y += 5;
  pdf.text(`Emitido em ${emitido}. ${r.quantidade === 1 ? '1 acerto' : `${r.quantidade} acertos`}${completo && r.quantidade > 0 ? ', com o demonstrativo de cada um nas páginas seguintes' : ''}.`, margem, y);
  y += 5;
  pdf.setFontSize(8.5);
  pdf.setTextColor(90);
  // As fontes do PDF não têm o sinal de menos tipográfico: vai o hífen.
  const explicacao = pdf.splitTextToSize('Valores em reais. Saldo = saldo anterior + vales - despesas pagas pelo colaborador - créditos + descontos. '
    + 'Saldo negativo: a empresa pagou ao colaborador; positivo: o colaborador devolveu à empresa.', direita - margem);
  pdf.text(explicacao, margem, y);
  pdf.setTextColor(0);
  y += explicacao.length * 3.8 + 2;
  pdf.setLineWidth(0.3);
  pdf.line(margem, y, direita, y);
  y += 7;

  if (r.quantidade === 0) {
    pdf.setFontSize(10);
    pdf.text('Nenhum acerto fechado neste período.', margem, y);
    y += 8;
  }

  for (const g of r.grupos) {
    cabe(26);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10.5);
    pdf.text(`${nomeDaPessoa(g.colaborador)}${g.colaborador?.cpf ? ` · CPF ${formatarCpf(g.colaborador.cpf)}` : ''}`, margem, y);
    y += 5.5;
    cabecalhoDaTabela();
    emTabela = true;
    for (const a of g.acertos) {
      cabe(10);
      linhaDeValores({
        numero: `${a.numero}/${a.ano}`,
        data: formatarData(a.data),
        periodo: periodoCurto(a),
      }, { creditos: a.total_creditos, vales: a.total_vales, despesas: a.total_despesas, descontos: a.total_descontos, saldo: a.saldo });
      y += 3.8;
      linhaPequena(`${Number(a.saldo_anterior) !== 0 ? `Saldo anterior ${valor(a.saldo_anterior)} · ` : ''}${situacaoNoRelatorio(a)}`);
    }
    cabe(14);
    pdf.setLineWidth(0.2);
    pdf.line(col.periodo, y - 3.8, direita, y - 3.8);
    linhaDeValores({ numero: 'Total', periodo: g.acertos.length === 1 ? '1 acerto' : `${g.acertos.length} acertos` }, g.totais, { negrito: true });
    y += 3.8;
    linhaPequena(resumoDoDinheiro(g.totais));
    emTabela = false;
    y += 4;
  }

  if (r.grupos.length > 1) {
    cabe(16);
    pdf.setLineWidth(0.3);
    pdf.line(margem, y - 4, direita, y - 4);
    linhaDeValores({ numero: 'Total geral', periodo: `${r.quantidade} acertos` }, r.totais, { negrito: true });
    y += 3.8;
    linhaPequena(resumoDoDinheiro(r.totais));
    y += 4;
  }

  if (r.desfeitos.length > 0) {
    cabe(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.text('Acertos desfeitos no período (não valem e não entram nos totais)', margem, y);
    y += 5.5;
    for (const { acerto: a, colaborador } of r.desfeitos) {
      cabe(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8.5);
      const linhas = pdf.splitTextToSize(
        `Nº ${a.numero}/${a.ano} de ${formatarData(a.data)} · ${nomeDaPessoa(colaborador)} · motivo: ${a.reaberto_motivo || 'não informado'}`, direita - margem);
      pdf.text(linhas, margem, y);
      y += linhas.length * 4 + 1;
    }
  }

  if (completo) {
    for (const g of r.grupos) {
      for (const a of g.acertos) {
        pdf.addPage();
        desenharDemonstrativo(pdf, { ...dadosDoAcerto(a), acerto: a, empresa, colaborador: g.colaborador });
      }
    }
  }

  rodape(pdf, `relatório de acertos de ${formatarData(r.de)} a ${formatarData(r.ate)}`);
  return pdf.output('blob');
}

// "01/08 a 10/08/2026" (com o ano no começo também, se for outro).
export function periodoCurto(a) {
  if (!a.periodo_de || a.periodo_de === a.data) return formatarData(a.data);
  const inicio = a.periodo_de.slice(0, 4) === a.data.slice(0, 4) ? formatarData(a.periodo_de).slice(0, 5) : formatarData(a.periodo_de);
  return `${inicio} a ${formatarData(a.data)}`;
}

function resumoDoDinheiro(t) {
  const partes = [
    t.pago > 0 && `pago pela empresa ${numeroBR(t.pago, { casas: 2 })}`,
    t.devolvido > 0 && `devolvido à empresa ${numeroBR(t.devolvido, { casas: 2 })}`,
    t.levado !== 0 && `levado para o acerto seguinte ${numeroBR(t.levado, { casas: 2 })}`,
    t.emAberto !== 0 && `ainda em aberto ${numeroBR(t.emAberto, { casas: 2 })}`,
  ].filter(Boolean);
  return partes.length > 0 ? `${partes.join(' · ')}.` : 'Nada pago nem devolvido (acertos zerados).';
}
